import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { ROOMS } from "@/game/rooms";
import { objective } from "@/components/game/ProgressCounter";
import { DropOffChart, DailyChart } from "@/components/AdminCharts";

const CH = ["chapter_1", "chapter_2", "chapter_3"];
const date = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const ENDS = { chapter_1: "cave_tunnel:event:end", chapter_2: "living:event:ch2end", chapter_3: "outside:event:final" };

const Stat = ({ label, value, testid }) => (
  <div className="border-2 border-[#2E3440] bg-[#0A0B10] p-4" data-testid={testid}>
    <p className="font-blocky text-[8px] text-[#64748B] tracking-widest">{label}</p>
    <p className="font-blocky text-xl sm:text-2xl text-[#F3F4F6] mt-2">{value}</p>
  </div>
);

const Where = ({ chapter, p }) => {
  if (!p) return <span className="text-[#475569]">not started</span>;
  if (p.notes.includes(ENDS[chapter])) return <span className="text-[#4ade80]">✓ finished</span>;
  const [text] = objective(p.notes, p.inventory);
  return (
    <span className="text-[#9CA3AF]">
      <span className="text-[#F3F4F6]">{ROOMS[p.room]?.name || p.room}</span> · {text.replace("OBJECTIVE: ", "")}
      <span className="text-[#475569]"> · {date(p.updated_at)}</span>
    </span>
  );
};

const PlayerRow = ({ u }) => (
  <tr className="border-t border-[#1f2430] align-top" data-testid={`admin-player-${u.username}`}>
    <td className="py-3 pr-4 text-[#F3F4F6] whitespace-nowrap">@{u.username}<div className="text-[#475569] text-base">joined {date(u.created_at)}</div></td>
    <td className="py-3 pr-4 whitespace-nowrap">{u.owned.length ? u.owned.map((c) => c.replace("chapter_", "Ch")).join(", ") : "—"}
      {u.paid.length > 0 && <div className="text-[#DC2626] text-base">paid: {u.paid.map((c) => c.replace("chapter_", "Ch")).join(", ")}</div>}</td>
    {CH.map((c) => <td key={c} className="py-3 pr-4 min-w-[220px]"><Where chapter={c} p={u.progress[c]} /></td>)}
  </tr>
);

export default function AdminPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { api.get("/admin/stats").then((r) => setData(r.data)).catch((e) => setError(apiError(e))); }, []);
  const copy = () => { navigator.clipboard.writeText(data.signups.map((s) => s.email).join("\n")); toast.success("Emails copied"); };
  const t = data?.totals;
  return (
    <div className="min-h-screen px-4 sm:px-8 lg:px-12 py-8 max-w-[1800px] mx-auto pixel-text text-lg" data-testid="admin-page">
      <header className="flex items-center justify-between gap-4 mb-8">
        <h1 className="font-blocky text-base sm:text-2xl text-[#F3F4F6]">ADMIN</h1>
        <button className="pixel-btn ghost" onClick={() => nav("/")} data-testid="admin-back-button"><ArrowLeft size={14} className="inline mr-1" />Chapters</button>
      </header>
      {error && <p className="text-[#DC2626] text-xl" data-testid="admin-error">&gt; {error}</p>}
      {!data && !error && <p className="text-[#64748B]" data-testid="admin-loading">Loading...</p>}
      {data && <>
        <section className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="admin-totals">
          <Stat label="PLAYERS" value={t.players} testid="admin-stat-players" />
          <Stat label="NEW THIS WEEK" value={t.new_this_week} testid="admin-stat-new" />
          <Stat label="ACTIVE THIS WEEK" value={t.active_this_week} testid="admin-stat-active" />
          <Stat label="PAID PURCHASES" value={t.paid_purchases} testid="admin-stat-purchases" />
          <Stat label="REVENUE" value={`$${t.revenue.toFixed(2)}`} testid="admin-stat-revenue" />
          <Stat label="SUPPORTERS" value={t.supporters} testid="admin-stat-supporters" />
          <Stat label="FILM EMAILS" value={t.signups} testid="admin-stat-signups" />
        </section>
        <section className="mt-8 flex flex-wrap gap-3" data-testid="admin-sales">
          {CH.map((c) => <span key={c} className="border border-[#2E3440] px-3 py-1 text-[#9CA3AF]">{c.replace("chapter_", "Chapter ")}: <span className="text-[#F3F4F6]">{data.sales[c] || 0}</span> paid</span>)}
        </section>
        <DailyChart players={data.players} sales={data.sales_log || []} />
        <DropOffChart players={data.players} />
        <section className="mt-8">
          <p className="font-blocky text-[10px] text-[#DC2626] tracking-widest mb-3">PLAYERS · WHERE THEY ARE</p>
          <div className="overflow-x-auto border-2 border-[#2E3440] bg-[#0A0B10] px-4" data-testid="admin-players-table">
            <table className="w-full text-left">
              <thead><tr className="font-blocky text-[8px] text-[#64748B]"><th className="py-3 pr-4">PLAYER</th><th className="py-3 pr-4">OWNS</th>
                <th className="py-3 pr-4">CHAPTER 1</th><th className="py-3 pr-4">CHAPTER 2</th><th className="py-3 pr-4">CHAPTER 3</th></tr></thead>
              <tbody>{data.players.map((u) => <PlayerRow key={u.id} u={u} />)}</tbody>
            </table>
          </div>
        </section>
        <section className="mt-8 mb-16">
          <div className="flex items-center justify-between mb-3">
            <p className="font-blocky text-[10px] text-[#DC2626] tracking-widest">FILM UPDATE EMAILS ({data.signups.length})</p>
            {data.signups.length > 0 && <button className="pixel-btn ghost" onClick={copy} data-testid="admin-copy-emails-button"><Copy size={14} className="inline mr-1" />Copy all</button>}
          </div>
          <div className="border-2 border-[#2E3440] bg-[#0A0B10] p-4 max-h-80 overflow-y-auto" data-testid="admin-signups-list">
            {data.signups.length === 0 && <p className="text-[#64748B]">No signups yet.</p>}
            {data.signups.map((s) => <p key={s.email} className="text-[#F3F4F6]">{s.email} <span className="text-[#475569]">· {s.source} · {date(s.created_at)}</span></p>)}
          </div>
        </section>
      </>}
    </div>
  );
}
